import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, map, Observable, of, shareReplay, switchMap, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ORG_HEADER } from '../interceptors/org-header.interceptor';
import { OrgContextService } from '../user-context/org-context.service';

/** Why a session ended (POST /sessions/end `end_type`). */
export type SessionEndType = 'VOLUNTARY' | 'TIMEOUT';

interface SessionStartResponse {
  session_id: number;
}

const STORAGE_KEY = 'csd.session.id';

/** Cap on how long sign-out waits for /sessions/end before redirecting anyway. */
const END_TIMEOUT_MS = 3000;

/**
 * Session audit trail (FDD §7): POST /sessions/start once per login and
 * POST /sessions/end on sign-out, feeding the Session Audit Log. POST /auth/switch-org
 * also returns a session id; once adopted it is the one used until the next switch or
 * sign-out (and start() then has nothing to do).
 *
 * The session id lives in `sessionStorage`, matching the OIDC token's lifetime
 * (see FcraGateService): a same-tab refresh keeps the session, so start() is a
 * no-op until the next login.
 *
 * Both calls are best-effort and silent — a tracking failure must never block
 * sign-in or sign-out, or toast. They therefore skip the app's interceptors
 * (HttpBackend) and attach the bearer + org header here: through the error
 * interceptor, a 401 from /sessions/end would dispatch logout again and loop.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly oauthService = inject(OAuthService);
  private readonly orgContext = inject(OrgContextService);
  private readonly base = `${environment.API_URL}/sessions`;

  /**
   * The in-flight POST /sessions/start, so a sign-out that lands before it
   * answers can still end that session. Emits the id, or null on failure.
   */
  private pendingStart: Observable<number | null> | null = null;

  /** Start the login's session, unless one is already recorded for this tab. */
  start(): void {
    if (this.readId() !== null || this.pendingStart) {
      return;
    }
    const pending = this.http
      .post<SessionStartResponse>(`${this.base}/start`, null, { headers: this.headers() })
      .pipe(
        map((res) => {
          // Persist only if sign-out hasn't already claimed this start; end()
          // receives the id from the emission, not from storage.
          if (this.pendingStart === pending) {
            this.pendingStart = null;
            this.writeId(res.session_id);
          }
          return res.session_id;
        }),
        catchError(() => {
          if (this.pendingStart === pending) {
            this.pendingStart = null;
          }
          return of(null);
        }),
        shareReplay(1),
      );
    this.pendingStart = pending;
    pending.subscribe();
  }

  /**
   * End the current session. Always completes with a single emission — even on
   * error, timeout or when no session was started — so sign-out can chain on it.
   * A start still in flight is waited for (within the same timeout).
   */
  end(endType: SessionEndType): Observable<void> {
    const sessionId$ = this.pendingStart ?? of(this.readId());
    this.reset();
    return sessionId$.pipe(
      switchMap((sessionId) =>
        sessionId === null
          ? of(undefined)
          : this.http.post<void>(
              `${this.base}/end`,
              { session_id: sessionId, end_type: endType },
              { headers: this.headers() },
            ),
      ),
      timeout(END_TIMEOUT_MS),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  /** The recorded session id, or null (none yet, or its start is still in flight). */
  currentId(): number | null {
    return this.readId();
  }

  /** Use the session id switch-org returned from now on. A start still in flight is
   *  dropped, so its answer can't overwrite this id. */
  adopt(sessionId: number): void {
    this.pendingStart = null;
    this.writeId(sessionId);
  }

  /** Forget the recorded session (called on each new login). */
  reset(): void {
    this.pendingStart = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing persisted to clear.
    }
  }

  private headers(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.oauthService.getAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const orgId = this.orgContext.activeOrg()?.orgId;
    if (orgId != null) {
      headers = headers.set(ORG_HEADER, String(orgId));
    }
    return headers;
  }

  private readId(): number | null {
    try {
      const value = Number(sessionStorage.getItem(STORAGE_KEY));
      return Number.isInteger(value) && value > 0 ? value : null;
    } catch {
      return null;
    }
  }

  private writeId(sessionId: number): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(sessionId));
    } catch {
      // No persistence — sign-out simply won't have a session to end.
    }
  }
}
